import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Card,
  CardBody,
  CardHeader,
  Code,
  Heading,
  HStack,
  Image,
  Input,
  Text,
  Textarea,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { ImageIcon, MessageSquare, Mic, Volume2 } from 'lucide-react';
import { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { trpcAdmin } from '../lib/trpcAdmin';
import { withPermission } from '../withPermission';

// The AI routes deliberately return a generic, user-safe message — the detailed
// diagnostic (which env var / SSM parameter / region to fix) is logged
// server-side only, never sent to the client.
function showAiError(toast: ReturnType<typeof useToast>, title: string, message: string) {
  toast({
    title,
    description: message,
    status: 'error',
    duration: 5000,
    isClosable: true,
  });
}

export const AiPlaygroundPage = withPermission('canAccessDeveloperTools', () => {
  return (
    <Box p={6}>
      <VStack spacing={6} align="stretch">
        <Heading size="lg">
          <Trans>AI Playground</Trans>
        </Heading>
        <Text color="gray.600">
          <Trans>
            Test the AI capabilities provided by <Code>@duke-hq/libai</Code>. Requires valid AWS
            credentials with Bedrock access.
          </Trans>
        </Text>

        <TextGenerationCard />
        <ImageGenerationCard />
        <TextToSpeechCard />
        <TranscriptionCard />
      </VStack>
    </Box>
  );
});

// -- Text Generation ---------------------------------------------------------

function TextGenerationCard() {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [system, setSystem] = useState('');

  const mutation = trpcAdmin.ai.generateText.useMutation({
    onError: (error) => {
      showAiError(toast, t`Text generation failed`, error.message);
    },
  });

  const inputTokens = mutation.data?.usage.inputTokens ?? t`N/A`;
  const outputTokens = mutation.data?.usage.outputTokens ?? t`N/A`;

  return (
    <Card>
      <CardHeader>
        <HStack>
          <Box color="purple.500">
            <MessageSquare size={24} />
          </Box>
          <Heading size="md">
            <Trans>Text Generation</Trans>
          </Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="start">
          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>Prompt:</Trans>
            </Text>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t`e.g. Summarise the key concepts of workplace health and safety.`}
              rows={3}
            />
          </VStack>

          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>System prompt (optional):</Trans>
            </Text>
            <Input
              value={system}
              onChange={(e) => setSystem(e.target.value)}
              placeholder={t`e.g. You are an Australian VET course designer.`}
            />
          </VStack>

          <Button
            leftIcon={<MessageSquare size={16} />}
            onClick={() => mutation.mutate({ prompt, system: system || undefined })}
            isLoading={mutation.isPending}
            loadingText={t`Generating...`}
            colorScheme="purple"
            isDisabled={!prompt.trim()}
          >
            <Trans>Generate Text</Trans>
          </Button>

          {mutation.data ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box w="full">
                <AlertTitle>
                  <Trans>Result</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Text whiteSpace="pre-wrap" mt={2}>
                    {mutation.data.text}
                  </Text>
                  <HStack mt={3} spacing={4}>
                    <Code>
                      <Trans>Input tokens: {inputTokens}</Trans>
                    </Code>
                    <Code>
                      <Trans>Output tokens: {outputTokens}</Trans>
                    </Code>
                  </HStack>
                </AlertDescription>
              </Box>
            </Alert>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
}

// -- Image Generation --------------------------------------------------------

function ImageGenerationCard() {
  const toast = useToast();
  const [prompt, setPrompt] = useState('');
  const [negativePrompt, setNegativePrompt] = useState('');

  const mutation = trpcAdmin.ai.generateImage.useMutation({
    onError: (error) => {
      showAiError(toast, t`Image generation failed`, error.message);
    },
  });

  return (
    <Card>
      <CardHeader>
        <HStack>
          <Box color="purple.500">
            <ImageIcon size={24} />
          </Box>
          <Heading size="md">
            <Trans>Image Generation</Trans>
          </Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="start">
          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>Prompt:</Trans>
            </Text>
            <Textarea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              placeholder={t`e.g. A professional diagram showing the fire evacuation procedure`}
              rows={2}
            />
          </VStack>

          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>Negative prompt (optional):</Trans>
            </Text>
            <Input
              value={negativePrompt}
              onChange={(e) => setNegativePrompt(e.target.value)}
              placeholder={t`e.g. blurry, low quality, text`}
            />
          </VStack>

          <Button
            leftIcon={<ImageIcon size={16} />}
            onClick={() => mutation.mutate({ prompt, negativePrompt: negativePrompt || undefined })}
            isLoading={mutation.isPending}
            loadingText={t`Generating...`}
            colorScheme="purple"
            isDisabled={!prompt.trim()}
          >
            <Trans>Generate Image</Trans>
          </Button>

          {mutation.data ? (
            <Box mt={4}>
              <Image
                src={`data:${mutation.data.mediaType};base64,${mutation.data.base64}`}
                alt={t`Generated image`}
                maxW="512px"
                borderRadius="md"
                border="1px solid"
                borderColor="gray.200"
              />
            </Box>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
}

// -- Text-to-Speech ----------------------------------------------------------

function TextToSpeechCard() {
  const toast = useToast();
  const [text, setText] = useState('');
  const [voiceId, setVoiceId] = useState('');

  const mutation = trpcAdmin.ai.textToSpeech.useMutation({
    onError: (error) => {
      showAiError(toast, t`Text-to-speech failed`, error.message);
    },
  });

  return (
    <Card>
      <CardHeader>
        <HStack>
          <Box color="purple.500">
            <Volume2 size={24} />
          </Box>
          <Heading size="md">
            <Trans>Text-to-Speech</Trans>
          </Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="start">
          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>Text:</Trans>
            </Text>
            <Textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t`e.g. Welcome to your first module on workplace communication.`}
              rows={2}
            />
          </VStack>

          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>Voice ID (optional, default: Olivia):</Trans>
            </Text>
            <Input
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder={t`e.g. Olivia, Nicole, Russell`}
            />
          </VStack>

          <Button
            leftIcon={<Volume2 size={16} />}
            onClick={() => mutation.mutate({ text, voiceId: voiceId || undefined })}
            isLoading={mutation.isPending}
            loadingText={t`Generating...`}
            colorScheme="purple"
            isDisabled={!text.trim()}
          >
            <Trans>Generate Speech</Trans>
          </Button>

          {mutation.data ? (
            <Box mt={4}>
              {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
              <audio
                controls
                src={`data:${mutation.data.contentType};base64,${mutation.data.audioBase64}`}
              />
            </Box>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
}

// -- Transcription -----------------------------------------------------------

function TranscriptionCard() {
  const toast = useToast();
  const [s3Uri, setS3Uri] = useState('');

  const mutation = trpcAdmin.ai.transcribeAudio.useMutation({
    onError: (error) => {
      showAiError(toast, t`Transcription failed`, error.message);
    },
  });

  return (
    <Card>
      <CardHeader>
        <HStack>
          <Box color="purple.500">
            <Mic size={24} />
          </Box>
          <Heading size="md">
            <Trans>Transcription</Trans>
          </Heading>
        </HStack>
      </CardHeader>
      <CardBody>
        <VStack spacing={4} align="start">
          <Alert status="info" borderRadius="md">
            <AlertIcon />
            <AlertDescription>
              <Trans>
                The audio file must already exist in S3. This will start an AWS Transcribe job and
                poll until it completes (up to ~5 minutes).
              </Trans>
            </AlertDescription>
          </Alert>

          <VStack spacing={2} align="start" w="full">
            <Text fontWeight="medium">
              <Trans>S3 URI:</Trans>
            </Text>
            <Input
              value={s3Uri}
              onChange={(e) => setS3Uri(e.target.value)}
              placeholder={t`e.g. s3://duke-uploads/recordings/lecture-01.mp3`}
            />
          </VStack>

          <Button
            leftIcon={<Mic size={16} />}
            onClick={() => mutation.mutate({ s3Uri })}
            isLoading={mutation.isPending}
            loadingText={t`Transcribing...`}
            colorScheme="purple"
            isDisabled={!s3Uri.trim()}
          >
            <Trans>Transcribe Audio</Trans>
          </Button>

          {mutation.data ? (
            <Alert status="success" borderRadius="md">
              <AlertIcon />
              <Box w="full">
                <AlertTitle>
                  <Trans>Transcription Result</Trans>
                </AlertTitle>
                <AlertDescription>
                  <Text whiteSpace="pre-wrap" mt={2}>
                    {mutation.data.text}
                  </Text>
                  <Code mt={2}>
                    <Trans>Job: {mutation.data.jobName}</Trans>
                  </Code>
                </AlertDescription>
              </Box>
            </Alert>
          ) : null}
        </VStack>
      </CardBody>
    </Card>
  );
}
